/*
 * 极简的服务端资源 hook。
 *
 * 为什么不直接上 @tanstack/react-query：
 *   admin 用它是因为有大量表格、分页、乐观更新与失效联动。H5 这一侧目前
 *   只需要「取一次 / 失败 / 重试」，引入一个查询库会带来它自己的缓存、
 *   重试与在线检测策略，而离线态的呈现恰恰是本次要精确控制的部分。
 *   接后端并出现真实的缓存失效需求时再替换，替换点只有这一个文件。
 *
 * 缓存边界：按账号隔离由调用方通过 key 保证；退出登录时整棵树卸载即清空。
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError } from "../services/api-client";

export type ResourceState<T> =
  | { phase: "loading"; data: null; error: null }
  | { phase: "ready"; data: T; error: null }
  | { phase: "failed"; data: null; error: ApiError };

export interface Resource<T> {
  state: ResourceState<T>;
  reload: () => void;
  /** 保留当前 ready 数据的后台刷新，适合生成任务的短轮询。 */
  refresh: () => void;
}

export function useResource<T>(
  fetcher: (signal: AbortSignal) => Promise<T>,
  deps: readonly unknown[] = [],
): Resource<T> {
  const [state, setState] = useState<ResourceState<T>>({
    phase: "loading",
    data: null,
    error: null,
  });
  const [nonce, setNonce] = useState(0);
  const preserveOnNextLoad = useRef(false);

  // fetcher 通常是内联箭头函数，放进依赖会每次渲染都重新请求。
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    const preserve = preserveOnNextLoad.current;
    preserveOnNextLoad.current = false;
    if (!preserve) setState({ phase: "loading", data: null, error: null });

    fetcherRef
      .current(controller.signal)
      .then((data) => {
        if (cancelled) return;
        setState({ phase: "ready", data, error: null });
      })
      .catch((cause: unknown) => {
        if (cancelled || controller.signal.aborted) return;
        setState({
          phase: "failed",
          data: null,
          error:
            cause instanceof ApiError
              ? cause
              : new ApiError({
                  kind: "malformed",
                  message: String(cause),
                  requestId: "-",
                  retryable: false,
                }),
        });
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nonce, ...deps]);

  const reload = useCallback(() => {
    preserveOnNextLoad.current = false;
    setNonce((n) => n + 1);
  }, []);
  const refresh = useCallback(() => {
    preserveOnNextLoad.current = true;
    setNonce((n) => n + 1);
  }, []);

  return { state, reload, refresh };
}
